import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ENDPOINTS, resolveHtmlAssetUrls } from '../config';

/**
 * About Us page — renders CMS HTML (same pattern as PolicyPage).
 * Content uses embedded fx-about-* styles like policy pages (fx-policy-*).
 */
export default function About() {
    const [about, setAbout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchAbout = async () => {
            try {
                const res = await fetch(ENDPOINTS.ABOUT_US.PUBLIC);
                const data = await res.json();
                if (data.isOk && data.data) {
                    setAbout(data.data);
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error('Error fetching about page:', err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        fetchAbout();
    }, []);

    if (loading) {
        return (
            <main className="pt-[10px] pb-16">
                <div className="max-w-[1440px] mx-auto px-4 animate-pulse">
                    <div className="h-8 bg-muted rounded w-1/3 mb-8" />
                    <div className="space-y-3">
                        {[...Array(10)].map((_, i) => (
                            <div
                                key={i}
                                className={`h-4 bg-muted rounded ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`}
                            />
                        ))}
                    </div>
                </div>
            </main>
        );
    }

    if (notFound || !about) {
        return (
            <main className="pt-[10px] pb-16">
                <div className="max-w-[1440px] mx-auto px-4 text-center py-20">
                    <h1 className="text-2xl font-bold text-foreground mb-2">Page not found</h1>
                    <p className="text-muted-foreground mb-6">
                        About Us content is not available yet.
                    </p>
                    <Link
                        to="/"
                        className="inline-block px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all"
                    >
                        Back to Home
                    </Link>
                </div>
            </main>
        );
    }

    const plainDescription = (about.description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const metaTitle = about.metaTitle?.trim() || about.title || 'About Us';
    const metaDescription = about.metaDescription?.trim() || plainDescription.slice(0, 160);

    return (
        <main className="pt-[10px] pb-16 bg-card">
            <Helmet>
                <title>{metaTitle}</title>
                {metaDescription ? <meta name="description" content={metaDescription} /> : null}
                {about.metaKeywords?.trim() ? (
                    <meta name="keywords" content={about.metaKeywords.trim()} />
                ) : null}
            </Helmet>

            <div className="max-w-[1440px] mx-auto px-4">
                {/* Fallback / scoped styles for legacy simple HTML */}
                <style>{`
                    .about-content {
                        --brand-navy: #1E2C4A;
                        --brand-orange: #E94326;
                        --text-dark: #2D3436;
                        --text-muted: #636E72;
                        line-height: 1.8;
                        color: var(--text-dark);
                    }
                    .dark .about-content {
                        --brand-navy: oklch(var(--foreground));
                        --text-dark: oklch(var(--foreground));
                        --text-muted: oklch(var(--muted-foreground));
                    }
                    .about-content img { max-width: 100%; border-radius: 8px; margin: 12px 0; }
                    .about-content a { color: var(--brand-orange); text-decoration: none; }
                    .about-content a:hover { text-decoration: underline; }
                `}</style>

                <div
                    className="about-content leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(about.description) }}
                />
            </div>
        </main>
    );
}
